import { useEffect, useMemo, useState } from 'react';
import { IconClose } from '@/components/Icons';
import { NavLookupButton } from '@/components/NavLookup';
import { assetClassLabel, holdingLabel, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { formatMoney, formatPrice, formatRate } from '@/lib/money';
import { formatShortDate } from '@/lib/time';
import { describeMarketError } from '@/market/errors';
import { exchangeLabel, mutualFundUrl } from '@/market/exchange';
import { primeAsset } from '@/market/quoteService';
import { cashCandidate, cryptoCandidate, quickCandidates, resolveQuery, type Candidate } from '@/market/resolve';
import { tmxFund, type TmxFund } from '@/market/tmx';
import { convertFromUsd, usdPerUnit } from '@/market/value';
import { hasQuoteSource, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { MARKETS, isCdr, isWatched, looksLikeFundCode, quoteKey, type Asset, type Market } from '@/types';
import s from './addAsset.module.css';

interface AddAssetDialogProps {
  portfolioId: string;
  existing?: Asset; // edit mode
  onClose: () => void;
}

type ManualClass = 'stock' | 'etf' | 'fund';
const MANUAL_CLASSES: ManualClass[] = ['stock', 'etf', 'fund'];

/** The candidate an existing holding was added as, so edit mode shows the same card. */
function candidateOf(a: Asset): Candidate {
  if (a.assetClass === 'cash') return cashCandidate(a.currency.toUpperCase());
  if (a.assetClass === 'crypto') return cryptoCandidate(a.symbol.toUpperCase());
  if (a.assetClass === 'fund') return { kind: 'fund', symbol: a.symbol.toUpperCase(), name: a.name, market: 'CA', currency: 'CAD' };
  return { kind: a.assetClass, symbol: a.symbol.toUpperCase(), name: a.name, market: a.market ?? 'US', mic: a.mic, exchange: a.exchange, listingType: a.listingType ?? '', currency: a.currency.toUpperCase() };
}

export function AddAssetDialog({ portfolioId, existing, onClose }: AddAssetDialogProps) {
  const { t } = useT();
  const app = useAppState();
  const settings = useSettings();
  const portfolio = app.portfolios.find((p) => p.id === portfolioId);

  // One box: what the user typed, what the sources made of it, and the one candidate they picked.
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [usSearched, setUsSearched] = useState(hasQuoteSource(settings));
  const [picked, setPicked] = useState<Candidate | null>(existing ? candidateOf(existing) : null);
  // Fallback for a code no source knows: the user says what it is.
  const [manual, setManual] = useState(false);
  const [manualClass, setManualClass] = useState<ManualClass>('stock');
  const [manualMarket, setManualMarket] = useState<Market>('US');
  const [manualCode, setManualCode] = useState('');
  const [fundName, setFundName] = useState(existing?.assetClass === 'fund' ? existing.name : '');
  const [tmxHit, setTmxHit] = useState<{ code: string; found: TmxFund | null } | null>(null);

  const [quantity, setQuantity] = useState(existing ? String(existing.quantity) : '');
  const [costPrice, setCostPrice] = useState(existing?.costPrice != null ? String(existing.costPrice) : '');
  const [purchaseDate, setPurchaseDate] = useState(existing?.purchaseDate ?? '');
  const [note, setNote] = useState(existing?.note ?? '');
  const [manualPrice, setManualPrice] = useState(existing?.manualPrice != null ? String(existing.manualPrice) : '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // What the holding is, from the pick or from the manual fallback.
  const assetClass = picked?.kind ?? (manual ? manualClass : null);
  const market: Market | undefined = picked ? (picked.kind === 'stock' || picked.kind === 'etf' || picked.kind === 'fund' ? picked.market : undefined) : manual ? (manualClass === 'fund' ? 'CA' : manualMarket) : undefined;
  const isEquity = assetClass === 'stock' || assetClass === 'etf';
  const isFund = assetClass === 'fund';
  const isCash = assetClass === 'cash';
  const currency = picked ? picked.currency : market === 'CA' ? 'CAD' : 'USD';
  const symbol = picked ? picked.symbol : manualCode.trim().toUpperCase();
  const name = picked ? (picked.kind === 'fund' && fundName.trim() ? fundName.trim() : picked.name) : isFund ? fundName.trim() || symbol : symbol;
  // Everything is priced without a key (TMX Money covers US and Canadian listings and FundSERV funds); a manual
  // price is only ever needed for a code no source knows.
  const tmxFundPicked = picked?.kind === 'fund' && !!picked.fund;
  const hasPriceSource = !manual || isCash || assetClass === 'crypto' || (assetClass === 'fund' && tmxFundPicked);

  useEffect(() => {
    if (existing || picked || manual) return;
    const q = query.trim();
    if (!q) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await resolveQuery(q, controller.signal);
        setCandidates(r.candidates);
        setUsSearched(r.usSearched);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setError(describeMarketError(e));
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, existing, picked, manual]);

  // Manual fund entry still tries TMX Money once the code looks like a FundSERV code.
  useEffect(() => {
    if (!manual || manualClass !== 'fund' || !looksLikeFundCode(manualCode)) {
      setTmxHit(null);
      return;
    }
    const code = manualCode.trim().toUpperCase();
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const found = await tmxFund(code, controller.signal);
        setTmxHit({ code, found });
        if (found) {
          setFundName((n) => (n.trim() && n.trim() !== code ? n : found.name));
          setManualPrice(String(found.quote.price));
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setTmxHit(null);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [manual, manualClass, manualCode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (c: Candidate) => {
    setPicked(c);
    setCandidates([]);
    setManual(false);
    if (c.kind === 'fund') {
      setFundName(c.name);
      if (c.fund) setManualPrice(String(c.fund.quote.price));
    }
  };
  const unpick = () => {
    setPicked(null);
    setQuery('');
    setFundName('');
    setManualPrice('');
  };

  const qty = Number(quantity);
  // Quantity 0 is a watch item (see isWatched): priced and listed, but valued at nothing and kept out of the analysis.
  const watching = quantity.trim() !== '' && qty === 0;
  const livePrice = useMemo(() => {
    if (!assetClass) return null;
    if (assetClass === 'cash') return usdPerUnit(currency, app.fx);
    if (assetClass === 'fund') return picked?.kind === 'fund' && picked.fund ? picked.fund.quote.price : manualPrice ? Number(manualPrice) : null;
    const q = app.quotes[quoteKey({ assetClass, symbol, currency, market })];
    return q?.price ?? (manualPrice ? Number(manualPrice) : null);
  }, [assetClass, currency, symbol, market, app.quotes, app.fx, manualPrice, picked]);
  const previewUsd = livePrice != null && qty > 0 ? livePrice * qty : null;
  const ccy = settings.displayCurrency;

  const valid = !!assetClass && quantity.trim() !== '' && Number.isFinite(qty) && qty >= 0 && symbol.length > 0 && (!isEquity || hasPriceSource || Number(manualPrice) > 0 || !!existing) && (!isFund || tmxFundPicked || Number(manualPrice) > 0);

  const submit = async () => {
    if (!valid || saving || !assetClass) return;
    setSaving(true);
    setError(null);
    try {
      const listing = picked && (picked.kind === 'stock' || picked.kind === 'etf') ? picked : null;
      const base = {
        portfolioId,
        assetClass,
        symbol,
        name,
        exchange: isEquity ? (listing?.exchange ?? existing?.exchange) : undefined,
        market: isEquity || isFund ? market : undefined,
        mic: isEquity ? (listing?.mic ?? existing?.mic) : undefined,
        listingType: isFund ? 'Mutual Fund' : isEquity ? (listing?.listingType || existing?.listingType) : undefined,
        currency,
        quantity: qty,
        costPrice: costPrice ? Number(costPrice) : undefined,
        costCurrency: costPrice ? (isEquity || isFund ? currency : 'USD') : undefined,
        purchaseDate: purchaseDate || undefined,
        note: note.trim() || undefined,
        manualPrice: (isEquity || isFund) && manualPrice ? Number(manualPrice) : undefined,
        manualPriceAt: (isEquity || isFund) && manualPrice ? (existing?.manualPrice === Number(manualPrice) ? existing.manualPriceAt : Date.now()) : undefined,
      };
      let saved: Asset;
      if (existing) {
        await appStore.updateAsset(existing.id, base);
        saved = { ...existing, ...base };
      } else {
        saved = await appStore.addAsset(base);
      }
      onClose();
      void primeAsset(saved);
    } catch (e) {
      setError(describeMarketError(e));
    } finally {
      setSaving(false);
    }
  };

  const kindLabel = (c: Candidate) => (c.kind === 'stock' || c.kind === 'etf' ? holdingLabel({ assetClass: c.kind, market: c.market, listingType: c.listingType }) : c.kind === 'fund' ? t('class.fundCA') : assetClassLabel(c.kind));
  const venueLabel = (c: Candidate) => (c.kind === 'stock' || c.kind === 'etf' ? exchangeLabel(c.mic ?? c.exchange) ?? c.exchange ?? t(`market.${c.market}` as const) : c.kind === 'fund' ? 'FundSERV' : c.kind === 'crypto' ? 'Coinbase' : t('source.ecb'));
  const pickedIsCdr = !!picked && (picked.kind === 'stock' || picked.kind === 'etf') && isCdr({ market: picked.market, listingType: picked.listingType });
  const q = query.trim();
  const showQuick = !existing && !picked && !manual && !q;
  const showNoResults = !existing && !picked && !manual && q.length >= 2 && !searching && candidates.length === 0;

  return (
    <div className={s.backdrop} onClick={onClose}>
      <div className={s.dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className={s.head}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span className={`serif ${s.title}`}>{existing ? t('add.editTitle') : t('add.title')}</span>
            {portfolio && <span className="muted" style={{ fontSize: 13 }}>{existing ? portfolio.name : t('add.to', { name: portfolio.name })}</span>}
          </div>
          <button type="button" className="btn quiet" style={{ padding: '8px 10px' }} onClick={onClose} aria-label={t('common.close')}><IconClose size={16} /></button>
        </header>

        <div className={s.body}>
          {/* Step 1: say what it is — by code or name; the sources tell us the kind and the market. */}
          {!picked && !manual && !existing && (
            <div className={s.field}>
              <span className="label">{t('add.what')}</span>
              <input className="input" placeholder={t('add.whatPlaceholder')} value={query} autoFocus onChange={(e) => setQuery(e.target.value)} />
              {candidates.length > 0 && (
                <div className={s.results}>
                  {candidates.map((c) => (
                    <button key={`${c.kind}:${c.symbol}:${'market' in c ? c.market : ''}`} type="button" className={s.res} onClick={() => pick(c)}>
                      <span className="num" style={{ fontWeight: 600 }}>{c.symbol}</span>
                      <span>
                        {c.name} <span className="muted">· {venueLabel(c)}</span>
                        {(c.kind === 'stock' || c.kind === 'etf') && isCdr({ market: c.market, listingType: c.listingType }) && <span className="badge" style={{ marginLeft: 8 }}>CDR</span>}
                        {c.kind === 'fund' && c.fund && <span className="muted num" style={{ marginLeft: 8, fontSize: 12 }}>{t('add.navShort', { nav: formatPrice(c.fund.quote.price), date: formatShortDate(c.fund.quote.asOf) })}</span>}
                      </span>
                      <span className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{kindLabel(c)}</span>
                    </button>
                  ))}
                </div>
              )}
              <span className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>
                {searching ? t('add.searching') : usSearched ? t('add.whatHint') : t('add.whatHintNoUsKey')}
              </span>
              {showQuick && (
                <div className={s.quick}>
                  <span className="muted" style={{ fontSize: 12 }}>{t('add.quick')}</span>
                  {quickCandidates().map((c) => <button key={`${c.kind}:${c.symbol}`} type="button" className="btn quiet" style={{ padding: '5px 11px', fontSize: 12 }} onClick={() => pick(c)}>{c.kind === 'cash' ? t('list.cashName', { ccy: c.symbol }) : c.symbol}</button>)}
                </div>
              )}
              {showNoResults && (
                <div className={s.suggest}>
                  <span className="muted" style={{ fontSize: 12 }}>{t('add.noResults', { code: q.toUpperCase() })}</span>
                  <button type="button" className="btn quiet" onClick={() => { setManual(true); setManualCode(q.toUpperCase()); setManualClass(looksLikeFundCode(q) ? 'fund' : 'stock'); setManualMarket(looksLikeFundCode(q) ? 'CA' : 'US'); }}>{t('add.manualToggle')}</button>
                </div>
              )}
            </div>
          )}

          {/* Step 1, by hand: only when no source knew the code. */}
          {manual && !picked && (
            <>
              <div className={s.field}>
                <span className="label">{t('add.type')}</span>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div className="seg">
                    {MANUAL_CLASSES.map((c) => <button key={c} type="button" className={cx(manualClass === c && 'on')} onClick={() => setManualClass(c)}>{assetClassLabel(c)}</button>)}
                  </div>
                  {manualClass !== 'fund' && (
                    <div className="seg">
                      {MARKETS.map((m) => <button key={m} type="button" className={cx(manualMarket === m && 'on')} onClick={() => setManualMarket(m)}>{t(`market.${m}` as const)}</button>)}
                    </div>
                  )}
                  <button type="button" className="btn link" style={{ fontSize: 12 }} onClick={() => setManual(false)}>{t('add.backToSearch')}</button>
                </div>
                {(manualClass === 'etf' || manualClass === 'fund') && <span className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>{t(manualClass === 'etf' ? 'add.classHint.etf' : 'add.classHint.fund')}</span>}
              </div>
              <div className={s.grid2}>
                <div className={s.field}>
                  <span className="label">{manualClass === 'fund' ? t('add.fundCode') : t('add.symbol')}</span>
                  <input className="input num" placeholder={manualClass === 'fund' ? 'FID5982' : 'AAPL'} value={manualCode} autoFocus onChange={(e) => setManualCode(e.target.value.toUpperCase())} />
                </div>
                {manualClass === 'fund' && (
                  <div className={s.field}>
                    <span className="label">{t('add.fundName')}</span>
                    <input className="input" placeholder={t('add.fundNamePlaceholder')} value={fundName} onChange={(e) => setFundName(e.target.value)} />
                  </div>
                )}
              </div>
              {manualClass === 'fund' && (
                <div className={s.field}>
                  <span className="label">{t('add.nav', { ccy: 'CAD' })}</span>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'start' }}>
                    <input className="input num" inputMode="decimal" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="0.00" />
                    {!tmxHit?.found && <NavLookupButton fund={{ symbol: manualCode.trim(), name: fundName.trim() || manualCode.trim(), market: 'CA', currency: 'CAD' }} disabled={!looksLikeFundCode(manualCode)} onResult={(r) => { setManualPrice(String(r.nav)); if (!fundName.trim() && r.name) setFundName(r.name); }} />}
                  </div>
                  {tmxHit?.found && <span className="muted" style={{ fontSize: 12 }}>{t('add.tmxFund', { name: tmxHit.found.name, nav: formatPrice(tmxHit.found.quote.price), ccy: tmxHit.found.quote.currency, date: formatShortDate(tmxHit.found.quote.asOf) })}</span>}
                  {tmxHit && !tmxHit.found && <span className="down" style={{ fontSize: 12 }}>{t('add.tmxFundMiss', { code: tmxHit.code })}</span>}
                  <span className="muted" style={{ fontSize: 12 }}>{t('add.navNote')} {looksLikeFundCode(manualCode) && <a href={mutualFundUrl(manualCode, 'CA')} target="_blank" rel="noopener noreferrer">{t('add.navLookup', { code: manualCode.trim().toUpperCase() })}</a>}</span>
                </div>
              )}
            </>
          )}

          {/* What was picked, then the holding itself. */}
          {picked && (
            <div className={s.pickedCard}>
              <div className={s.pickedMain}>
                <span className="serif num" style={{ fontSize: 22, fontWeight: 500 }}>{picked.kind === 'cash' ? picked.name : picked.symbol}</span>
                <span style={{ minWidth: 0 }}>
                  {picked.kind !== 'cash' && <span>{name}</span>}
                  <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                    {[kindLabel(picked), venueLabel(picked)].join(' · ')}
                    {picked.kind === 'fund' && picked.fund && ` · ${t('add.navShort', { nav: formatPrice(picked.fund.quote.price), date: formatShortDate(picked.fund.quote.asOf) })}`}
                  </span>
                </span>
              </div>
              {pickedIsCdr && <span className="badge">CDR</span>}
              {!existing && <button type="button" className="btn link" style={{ fontSize: 12 }} onClick={unpick}>{t('add.change')}</button>}
            </div>
          )}
          {pickedIsCdr && <span className="muted" style={{ fontSize: 12, marginTop: -8 }}>{t('add.cdrHint')}</span>}
          {picked?.kind === 'crypto' && !existing && <span className="muted" style={{ fontSize: 12, marginTop: -8 }}>{t('add.coinNote')}</span>}

          {(picked || manual) && (
            <>
              <div className={s.grid3}>
                <div className={s.field}>
                  <span className="label">{isCash ? t('add.amount') : isFund ? t('add.units') : t('add.quantity')}</span>
                  <input className="input num" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" autoFocus={!!picked} />
                </div>
                {!isCash && (
                  <div className={s.field}>
                    <span className="label">{t('add.cost', { ccy: isEquity || isFund ? currency : 'USD' })}</span>
                    <input className="input num" inputMode="decimal" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} placeholder={t('add.costPlaceholder')} />
                  </div>
                )}
                <div className={s.field}>
                  <span className="label">{t('add.date')}</span>
                  <input className="input num" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
                </div>
              </div>
              <span className="muted" style={{ fontSize: 12, lineHeight: 1.6, marginTop: -8 }}>{existing && isWatched(existing) ? t('add.watchToHolding') : t('add.watchHint')}</span>

              {isEquity && !hasPriceSource && (
                <div className={s.field}>
                  <span className="label">{t('add.manualPrice', { ccy: currency })}</span>
                  <input className="input num" inputMode="decimal" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder={t('add.manualPlaceholder')} />
                </div>
              )}
              {/* Editing a fund: the name is the user's own, and the NAV can still be overridden by hand (the detail page's "update NAV"). */}
              {isFund && existing && (
                <div className={s.field}>
                  <span className="label">{t('add.fundName')}</span>
                  <input className="input" placeholder={t('add.fundNamePlaceholder')} value={fundName} onChange={(e) => setFundName(e.target.value)} />
                </div>
              )}
              {isFund && existing && (
                <div className={s.field}>
                  <span className="label">{t('add.nav', { ccy: currency })}</span>
                  <input className="input num" inputMode="decimal" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="0.00" />
                  <span className="muted" style={{ fontSize: 12 }}>{t('add.navNote')}</span>
                </div>
              )}

              <div className={s.field}>
                <span className="label">{t('add.note')}</span>
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('add.notePlaceholder')} />
              </div>

              <div className={s.preview}>
                <span className="muted">
                  {livePrice == null
                    ? t('add.previewNone')
                    : isCash
                      ? t('add.previewRate', { rate: formatRate(livePrice) })
                      : t('add.previewPrice', { price: formatPrice(livePrice) })}
                </span>
                <span>{t('add.value')} <span className="serif num" style={{ fontSize: 18 }}>{watching ? <span className="muted" style={{ fontSize: 13, fontFamily: 'var(--font-sans)' }}>{t('add.previewWatch')}</span> : formatMoney(previewUsd == null ? null : convertFromUsd(previewUsd, ccy, app.fx), ccy)}</span></span>
              </div>
            </>
          )}
          {error && <span className={s.err}>{error}</span>}
        </div>

        <footer className={s.foot}>
          <span className="muted" style={{ fontSize: 12 }}>{t('add.footNote')}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn quiet" onClick={onClose}>{t('common.cancel')}</button>
            <button type="button" className="btn primary" disabled={!valid || saving} onClick={() => void submit()}>{existing ? t('common.save') : watching ? t('add.submitWatch') : t('add.submit')}</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
